import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { cn } from "@/lib/utils";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/input-otp";
import { REGEXP_ONLY_DIGITS_AND_CHARS } from "input-otp";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Form } from "@/components/ui/form";
import { emailVerificationOtp } from "@/schemas/EmailVerificationOtp";
import { z } from "zod";
import { LoaderCircle } from "lucide-react";
import { Button } from "../ui/button";

const EmailVerificationForm: React.FC<{
  className?: string;
  completeSignUp: (verificationCode: string) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  resendVerificationCode: (email: string) => Promise<boolean>;
  ref: React.Ref<{ resetOtp: () => void }>;
  email: string;
}> = forwardRef(
  (
    { className, completeSignUp, loading, setLoading, resendVerificationCode, email, ...props },
    ref
  ) => {
    const [countdown, setCountdown] = useState(0);
    const [optLoading, setOptLoading] = useState(false);
    const form = useForm<z.infer<typeof emailVerificationOtp>>({
      resolver: zodResolver(emailVerificationOtp),
      defaultValues: {
        otp: "",
      },
    });

    const otp = form.watch("otp");
    useImperativeHandle(ref, () => ({
      resetOtp: () => {
        form.setValue("otp", "");
      },
    }));

    useEffect(() => {
      if (otp?.length === 6 && !loading) {
        form.handleSubmit(async ({ otp }) =>  await completeSignUp(otp))();
        form.reset();
      }
    }, [otp, form, completeSignUp, loading]);

    useEffect(() => {
      let timer: NodeJS.Timeout;
      if (countdown > 0) {
        timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      }
      return () => clearTimeout(timer);
    }, [countdown]);

    const handleResendCode = async () => {
      try {
        setOptLoading(true);

        const success = await resendVerificationCode(email);
        if (success) {
          setCountdown(60);
        } else {
          setCountdown(0);
        }
      } catch (error) {
        console.log("error::::: ", error);
      } finally {
        setOptLoading(false);
      }
    };

    const slotClassName =
      "focus:outline-none data-[active=true]:ring-2 data-[active=true]:ring-black/80 data-[active=true]:border-black";

    return (
      <div
        className={cn(
          "flex flex-col h-full w-full items-center justify-center gap-6 rounded-lg shadow-sm py-20 md:py-0",
          className
        )}
        {...props}
      >
        <Card className="flex flex-col w-full max-w-xl py-20 mx-auto">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl mb-1">Verify Your Email</CardTitle>
            <CardDescription className="text-palette-text">
              Enter the 6-digit code sent to your email
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center px-6 py-4">
            <Form {...form}>
              <FormField
                control={form.control}
                name="otp"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="sr-only">Verification code</FormLabel>
                    <FormControl>
                      <div className="flex flex-col items-center justify-center w-full">
                        <InputOTP
                          {...field}
                          maxLength={6}
                          pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                          autoFocus
                          className="gap-2"
                          disabled={loading}
                        >
                          <InputOTPGroup className="flex justify-center">
                            {[0, 1, 2, 3, 4, 5].map((index) => (
                              <InputOTPSlot key={index} index={index} className={slotClassName} />
                            ))}
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-500 text-sm " />
                    <div className="flex space-x-2 items-center justify-center my-2 relative">
                      <div className="flex space-x-2 items-center justify-center my-2 relative">
                        {loading ? (
                          <div className="absolute translate-y-3 flex items-center justify-center z-10">
                            <LoaderCircle className="mr-2 h-5 w-5 animate-spin text-black" />
                            <p className="text-sm text-gray-700">Verifying...</p>
                          </div>
                        ) : optLoading ? (
                          <div className="absolute translate-y-3 flex items-center justify-center z-10">
                            <LoaderCircle className="mr-2 h-5 w-5 animate-spin text-black" />
                            <p className="text-sm text-gray-700">Resending...</p>
                          </div>
                        ) : (
                          <Button
                            className="absolute translate-y-3 text-sm"
                            onClick={handleResendCode}
                            disabled={countdown > 0}
                          >
                            {countdown > 0 ? `Resend Code (${countdown}s)` : "Resend Code"}
                          </Button>
                        )}
                      </div>
                    </div>
                  </FormItem>
                )}
              />
            </Form>
          </CardContent>
        </Card>
      </div>
    );
  }
);

export default EmailVerificationForm;
